import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { Checkbox, Paper, TableBody, TableRow, Typography, Button, Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS } from "date-fns/esm/locale";
import React, { useEffect, useState } from "react";

import useService from "../../../../../hooks/useService";
import HNSColors from "../../../../ui library/HNSColors";
import { formatAppointments } from "../helper";
import { actionButtonSizeSx, sectionSubtitleSx } from "../styles";

import {
  StyledTableCell,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
} from "./AssociateAppointmentsGrid.styled";
import NewAppointmentForm from "./NewAppointment";
import NewServiceForm from "./NewService";

const headerStyle = {
  fontWeight: "bold",
  fontSize: "16px",
  textAlign: "left",
  minWidth: "20px",
};
const cellStyle = { fontSize: "16px", textAlign: "left" };
const AssociateAppointmentsGrid = ({
  appointments,
  associatedAppointmentsServices,
  setAssociatedAppointmentsServices,
  associatedAppointmentsIds,
  setAssociatedAppointmentsIds,
  newAppointments,
  setNewAppointments,
  schema,
  title,
  locked = false,
}) => {
  const serviceSchema = schema["#/components/schemas/Bill"];
  serviceSchema?.properties?.service &&
    (serviceSchema.properties.service.typeProperties = {
      searchable: true,
      resLimit: 15,
    });
  const appointmentSchema = schema["#/components/schemas/RelatedAppointment"];

  const enumRepository = useService("repositories/enum-repository");

  // Fetch service options once here to avoid fetching in each NewService
  const providerTypeProp = appointmentSchema?.properties?.provider_type;
  const providerTypeEnumUrl =
    typeof providerTypeProp?.enum === "string" ? providerTypeProp.enum : undefined;

  const [providerTypeOptions, setProviderTypeOptions] = useState([]);
  useEffect(() => {
    const initOptions = async () => {
      try {
        const respProviderType = await enumRepository.query({
          urlPath: providerTypeEnumUrl.replace("/api/", ""),
        });
        setProviderTypeOptions(
          (respProviderType || []).map(({ key, value, label }) => ({
            key,
            value: value || label,
          }))
        );
      } catch (error) {
        console.error("Something went wrong fetching options", error);
      }
    };

    initOptions();
  }, [providerTypeEnumUrl]);

  const getAddedServices = (appointmentId) =>
    associatedAppointmentsServices?.[appointmentId]?.administered_services || [];

  const getServicesAmount = (appointment) => {
    const added = getAddedServices(appointment.id);
    return added.reduce((acc, service) => acc + Number(service.price || 0), 0);
  };

  const calculateRunningTotal = () => {
    let total = 0;
    formattedAppointments.forEach((appointment) => {
      if (associatedAppointmentsIds.includes(appointment.id)) {
        const servicesAmount = getServicesAmount({ id: appointment.id });
        total += servicesAmount;
      }
    });

    newAppointments.forEach((appointment) => {
      const servicesAmount = (appointment.administered_services || []).reduce(
        (acc, service) => acc + Number(service.price || service.amount || 0),
        0
      );
      total += servicesAmount;
    });

    return total;
  };

  const handleToggleAppointment = (appointmentId) => {
    setAssociatedAppointmentsIds((prev) => {
      const exists = prev.includes(appointmentId);
      if (exists) {
        return prev.filter((id) => id !== appointmentId);
      }
      return [...prev, appointmentId];
    });
  };

  const handleAddServiceToExistingAppointment = (appointmentId) => {
    setAssociatedAppointmentsServices((prev) => {
      const current = prev?.[appointmentId]?.administered_services || [];
      const next = [...current, { id: null, service: "", price: "" }];
      const newApptList = {
        ...prev,
        [appointmentId]: { administered_services: next },
      };
      return newApptList;
    });
  };

  const updateExistingAppointmentService = (appointmentId, serviceId, updatedFields) => {
    setAssociatedAppointmentsServices((prev) => {
      const current = prev?.[appointmentId]?.administered_services || [];
      const next = current.map((s, index) =>
        index === serviceId ? { ...s, ...updatedFields } : s
      );
      return { ...prev, [appointmentId]: { administered_services: next } };
    });
  };

  const removeExistingAppointmentService = (appointmentId, serviceId) => {
    setAssociatedAppointmentsServices((prev) => {
      const current = prev?.[appointmentId]?.administered_services || [];
      const next = current.filter((_, index) => index !== serviceId);
      return { ...prev, [appointmentId]: { administered_services: next } };
    });
  };

  const formattedAppointments = formatAppointments(appointments);

  const handleAddAppointment = () => {
    const newObj = {
      id: Date.now() + Math.random(),
      associated: true,
      appointment_date: new Date(),
      appointment_time: null,
      provider_type: null,
      administered_services: [],
    };
    setNewAppointments((forms) => [...forms, newObj]);
  };
  const handleRemoveAppointment = (appointments, index) => {
    const newAppointments = appointments.filter((_, i) => i !== index);
    setNewAppointments(newAppointments);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <Box className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="mb-2 mt-2 flex items-center justify-between">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: HNSColors.neutrals[100],
              textAlign: "left",
              fontFamily: "Roboto",
            }}
          >
            {title}
          </Typography>
          <hr />
          <br></br>
          <Typography sx={{ fontSize: "1.25rem" }}>
            <b>Received Bill Charges:</b> ${calculateRunningTotal().toFixed(2)}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            endIcon={<AddCircleOutlineOutlined />}
            sx={{
              ...actionButtonSizeSx,
              background: HNSColors.secondary[500],
              "&:hover": { background: HNSColors.secondary[600] },
            }}
            onClick={handleAddAppointment}
            disabled={locked}
          >
            New Appointment
          </Button>
        </div>
        <Typography variant="subtitle1" sx={sectionSubtitleSx}>
          Existing Appointments
        </Typography>
        {formattedAppointments.length === 0 ? (
          <div className="text-center">No scheduled appointments yet</div>
        ) : (
          <StyledTableContainer data-testid="styled-table-container" component={Paper}>
            <StyledTable>
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell sx={{ ...headerStyle, color: "Background" }}>
                    Associated
                  </StyledTableCell>
                  <StyledTableCell sx={headerStyle}>Provider Type</StyledTableCell>
                  <StyledTableCell sx={headerStyle}>Date</StyledTableCell>
                  <StyledTableCell sx={headerStyle}>Time</StyledTableCell>
                  <StyledTableCell sx={headerStyle}>Amount</StyledTableCell>
                  <StyledTableCell sx={{ ...headerStyle, color: "Background" }}>
                    Add Service
                  </StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {formattedAppointments.map((fa) => {
                  const isChecked = associatedAppointmentsIds.includes(fa.id);
                  const services = getAddedServices(fa.id);
                  return (
                    <React.Fragment key={fa.id}>
                      <TableRow>
                        <StyledTableCell sx={cellStyle}>
                          <Checkbox
                            className="checkbox-included"
                            data-testid="checkbox-included"
                            checked={isChecked}
                            onChange={() => handleToggleAppointment(fa.id)}
                            disabled={locked}
                          />
                        </StyledTableCell>
                        <StyledTableCell sx={cellStyle}>
                          {
                            providerTypeOptions.find((option) => option.key === fa.provider_type)
                              ?.value
                          }
                        </StyledTableCell>
                        <StyledTableCell sx={cellStyle}>{fa.date}</StyledTableCell>

                        <StyledTableCell sx={cellStyle}>{fa.time}</StyledTableCell>
                        <StyledTableCell sx={cellStyle}>
                          ${getServicesAmount({ id: fa.id }).toFixed(2)}
                        </StyledTableCell>
                        {isChecked ? (
                          <StyledTableCell sx={cellStyle}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => handleAddServiceToExistingAppointment(fa.id)}
                              disabled={locked}
                              className="px-2 normal-case"
                              endIcon={<AddCircleOutlineOutlined />}
                              size="small"
                              sx={{
                                borderRadius: "500px",
                                color: HNSColors.secondary[500],
                                border: `1px solid ${HNSColors.secondary[500]}`,
                                "&:hover": {
                                  background: HNSColors.secondary[900],
                                  color: HNSColors.secondary[400],
                                  border: `1px solid ${HNSColors.secondary[900]}`,
                                },
                              }}
                            >
                              Add Service
                            </Button>
                          </StyledTableCell>
                        ) : (
                          <StyledTableCell sx={cellStyle}>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled
                              sx={{ display: "none" }}
                            >
                              Hidden Button
                            </Button>
                          </StyledTableCell>
                        )}
                      </TableRow>

                      {isChecked &&
                        services.length > 0 &&
                        services.map((service, index) => (
                          <TableRow key={service.id}>
                            <StyledTableCell colSpan={5} sx={{ paddingLeft: 4 }}>
                              <NewServiceForm
                                flatSchema={serviceSchema}
                                serviceForm={service}
                                onCommit={(updatedFields) => {
                                  updateExistingAppointmentService(fa.id, index, updatedFields);
                                }}
                                onRemove={() => removeExistingAppointmentService(fa.id, index)}
                              />
                            </StyledTableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        )}
        {newAppointments.length > 0 && (
          <>
            <Box sx={{ mt: 6, mb: 2 }}>
              <Typography variant="subtitle1" sx={sectionSubtitleSx}>
                New Appointments
              </Typography>
              {newAppointments.map((form, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 4,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <NewAppointmentForm
                    newAppointment={form}
                    setNewAppointments={setNewAppointments}
                    flatSchema={appointmentSchema}
                    servicesAmount={form.administered_services
                      .reduce(
                        (sum, service) => sum + Number(service.price || service.amount || 0),
                        0
                      )
                      .toFixed(2)}
                    providerTypeOptions={providerTypeOptions}
                    onRemove={() => {
                      handleRemoveAppointment(newAppointments, index);
                    }}
                  />

                  {(form.administered_services || []).map((service) => (
                    <Box key={service.id} sx={{ mt: 2 }}>
                      <NewServiceForm
                        flatSchema={serviceSchema}
                        serviceForm={service}
                        onCommit={(updatedFields) => {
                          setNewAppointments((prev) =>
                            prev.map((appt) =>
                              appt.id === form.id
                                ? {
                                    ...appt,
                                    administered_services: (appt.administered_services || []).map(
                                      (s) => (s.id === service.id ? { ...s, ...updatedFields } : s)
                                    ),
                                  }
                                : appt
                            )
                          );
                        }}
                        onRemove={() => {
                          setNewAppointments((prev) =>
                            prev.map((appt) =>
                              appt.id === form.id
                                ? {
                                    ...appt,
                                    administered_services: (
                                      appt.administered_services || []
                                    ).filter((s) => s.id !== service.id),
                                  }
                                : appt
                            )
                          );
                        }}
                      />
                    </Box>
                  ))}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        setNewAppointments((prev) =>
                          prev.map((appt) =>
                            appt.id === form.id
                              ? {
                                  ...appt,
                                  administered_services: [
                                    ...(appt.administered_services || []),
                                    {
                                      id: Date.now() + Math.random(),
                                      service: "",
                                      price: "",
                                    },
                                  ],
                                }
                              : appt
                          )
                        )
                      }
                      endIcon={<AddCircleOutlineOutlined />}
                      size="large"
                      disabled={locked}
                      sx={{
                        ...actionButtonSizeSx,
                        color: HNSColors.secondary[500],
                        border: `1px solid ${HNSColors.secondary[500]}`,
                        "&:hover": {
                          background: HNSColors.secondary[900],
                          color: HNSColors.secondary[400],
                          border: `1px solid ${HNSColors.secondary[900]}`,
                        },
                      }}
                    >
                      Add Service
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AssociateAppointmentsGrid;
